import SegmentedButton from "@/components/SegmentedButton";
import Chip, { ChipSet } from "@/components/Chip";
import classNames from "classnames";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import {
  anchorForModeChange,
  buildPeriod,
  currentPeriod,
  filmstripLabel,
  isCurrentPeriod,
  neighboringPeriods,
  type PeriodMode,
  type PeriodWindow
} from "./periodWindow";

const MODE_OPTIONS: { label: string; value: PeriodMode }[] = [
  { label: "Week", value: "week" },
  { label: "Month", value: "month" },
  { label: "Year", value: "ytd" }
];

const FILMSTRIP_SCROLL_CLASS =
  "overflow-x-auto overscroll-x-contain [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden";

export type DateRangeScrollerProps = {
  value: PeriodWindow;
  onChange: (period: PeriodWindow) => void;
};

function DateRangeDivider() {
  return (
    <div
      className="h-6 w-px shrink-0 bg-sys-brand-outline-variant"
      aria-hidden="true"
      data-testid="date-range-scroller-divider"
    />
  );
}

function DateRangeChip({
  label,
  selected,
  ariaLabel,
  onClick
}: {
  label: string;
  selected: boolean;
  ariaLabel: string;
  onClick: () => void;
}) {
  return <Chip label={label} selected={selected} showCheckmark={false} aria-label={ariaLabel} onClick={onClick} />;
}

export default function DateRangeScroller({ value, onChange }: DateRangeScrollerProps) {
  const { isCompactLayout, isMobile } = useIsCompactLayout();
  const labelDensity = isMobile ? "narrow" : isCompactLayout ? "compact" : "full";
  // Year pills are short — always show two prior years. Week/month stay leaner on compact.
  const neighborCount = value.mode === "ytd" ? 2 : isMobile || isCompactLayout ? 1 : 2;
  const neighbors = neighboringPeriods(value, neighborCount).filter((neighbor) => !isCurrentPeriod(neighbor));
  const viewingCurrent = isCurrentPeriod(value);
  const current = currentPeriod(value.mode);
  const currentPillLabel = filmstripLabel(current, labelDensity);

  return (
    <div
      className={classNames("flex min-w-0 w-full gap-2", isCompactLayout ? "flex-col items-start" : "items-center")}
      data-testid="date-range-scroller"
    >
      <div className={classNames("shrink-0", isCompactLayout && "w-full")}>
        <SegmentedButton
          ariaLabel="Period mode"
          options={MODE_OPTIONS}
          value={value.mode}
          fullWidth={isCompactLayout}
          onChange={(mode) => {
            if (mode === "week" || mode === "month" || mode === "ytd") {
              onChange(buildPeriod(mode, anchorForModeChange(mode, value)));
            }
          }}
        />
      </div>
      {!isCompactLayout && <DateRangeDivider />}
      <div
        className={classNames(
          "flex min-w-0 items-center gap-2",
          isCompactLayout ? "w-full" : "flex-1",
          FILMSTRIP_SCROLL_CLASS
        )}
      >
        <div
          className="flex shrink-0 items-center gap-2"
          data-testid="date-range-scroller-filmstrip"
          role="group"
          aria-label={value.mode === "ytd" ? "Year" : "Period"}
        >
          <ChipSet>
            {neighbors.map((neighbor) => {
              const selected = neighbor.anchor.hasSame(
                value.anchor,
                value.mode === "week" ? "week" : value.mode === "month" ? "month" : "year"
              );
              return (
                <DateRangeChip
                  key={`${neighbor.mode}-${neighbor.anchor.toISODate()}`}
                  label={filmstripLabel(neighbor, labelDensity)}
                  selected={selected}
                  ariaLabel={neighbor.label}
                  onClick={() => onChange(neighbor)}
                />
              );
            })}
          </ChipSet>
          <DateRangeDivider />
          <ChipSet>
            <DateRangeChip
              label={currentPillLabel}
              selected={viewingCurrent}
              ariaLabel={`Jump to current period: ${current.label}`}
              onClick={() => onChange(current)}
            />
          </ChipSet>
        </div>
      </div>
      <p className="sr-only" aria-live="polite">
        {value.label}
      </p>
    </div>
  );
}
